<template>
  <InfuseModal :title="t('iam.organisations.chargingStations.modal.add.title')" @close="emit('close')">
    <div class="flex max-h-[650px] min-h-[650px] min-w-[965px] max-w-[965px] flex-col gap-4 overflow-y-auto px-5">
      <ChargingStationOverview
        context="within-action"
        :enable-selection="true"
        :columns="columns"
        :has-more-items="hasMore"
        :loading="isLoading"
        :removable="false"
        :rows="chargingStations"
        :page="0"
        :search-query="searchQuery || ''"
        :pre-selected="selectedStations"
        :show-selection-chips="true"
        :selection-chip-icon="['fal', 'charging-station']"
        @load="loadMore"
        @search="searchChargingStationsFn"
        @selected="(rows: ChargingStation[]) => (selectedStations = rows)"
      />
    </div>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" @click="emit('close')">
        {{ t('action.cancel') }}
      </InfuseButton>
      <InfuseButton
        data-testid="modal-confirm-button"
        :disabled="selectedStations.length === 0"
        @click="emit('add-charging-stations', selectedStations)"
      >
        {{ t('iam.organisations.chargingStations.modal.add.action.addSelected', { count: selectedStations.length }) }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { ChargingStation } from '@infuse/cpms/charging-station/store';
import { ChargingStationOverview } from '@infuse/cpms/charging-station/ui';
import { useChargingStationsNotInOrganisation } from '@infuse/cpms/location/store';
import { InfuseButton, InfuseModal } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  organisationId: string;
  /** Table columns — forwarded from the tab so the picker matches the main overview.
   *  Falls back to the table's default set when not provided. */
  columns?: UiTableColumn[];
}
const props = defineProps<Props>();

interface Emits {
  (e: 'add-charging-stations', chargingStations: ChargingStation[]): void;
  (e: 'close'): void;
}
const emit = defineEmits<Emits>();

const { chargingStations, hasMore, isLoading, organisationId, searchQuery, loadMore } =
  useChargingStationsNotInOrganisation();

const selectedStations = ref<ChargingStation[]>([]);

watch(
  () => props.organisationId,
  (id) => {
    selectedStations.value = [];
    if (id) {
      organisationId.value = id;
    }
  },
  { immediate: true }
);


function searchChargingStationsFn(search: string) {
  searchQuery.value = search || undefined;
}
</script>
